import { AuthError, readAuthError } from "./auth-error.js";
import { CLIENT_HEADERS } from "./generated/compatibility.js";

export interface AccountApi {
  readonly origin: string;
  readonly accountId: string;
  request(path: string, init?: RequestInit): Promise<Response>;
}
export type ApiTarget = string | AccountApi;

export function accountApi(origin: string, accountId: string, fetcher: typeof fetch = fetch, active: () => boolean = () => true): AccountApi {
  const changed = () => new AuthError("account_changed", "The active RecallVault account changed. Sign in again; queued work is kept on this browser.");
  return { origin, accountId, async request(path, init = {}) {
    if (!accountId || !active()) throw changed();
    const headers = new Headers(CLIENT_HEADERS);
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    headers.set("x-recallvault-account", accountId);
    const response = await fetcher(`${origin}${path}`, { ...init, credentials: "include", headers });
    if (!active()) throw changed();
    if (response.status === 401 || response.status === 403) throw await readAuthError(response) ?? changed();
    if (response.headers.get("x-recallvault-account") !== accountId) throw changed();
    // A streamed body may finish after the session changes. Return a complete
    // response only after both its identity and its bytes are still owned.
    const bytes = await response.arrayBuffer();
    if (!active()) throw changed();
    return new Response(response.status === 204 || response.status === 304 ? null : bytes,
      { status: response.status, statusText: response.statusText, headers: response.headers });
  } };
}

export function apiFetch(target: ApiTarget, path: string, init: RequestInit = {}, fetcher: typeof fetch = fetch): Promise<Response> {
  return typeof target === "string" ? fetcher(`${target}${path}`, { ...init, credentials: "include", headers: { ...CLIENT_HEADERS, ...init.headers } }) : target.request(path, init);
}
